/**
 * Helper: Query
 * -----------------------------------------------------------------------------
 * Query products and collections
 * - Add in alphabetical order.
 *
 */

import { formatProduct, formatCollection } from '~/helpers/format'

import product from '~/graphql/queries/product.gql'
import getProductById from '~/graphql/queries/product-id.gql'
import collection from '~/graphql/queries/collection.gql'
import { product_metafield_identifiers, variant_metafield_identifiers, collection_metafield_identifiers } from '~/helpers/metafield_identifiers'

const queries = { collection, product, getProductById }

/**
 * Option template for queries.
 * @param {Query} query - A GraphQL query.
 * @param {Object} variables - The variables to include in the query, such
 * as handle or id.
 * @returns {{variables: {country: string, handle}, query, locale: string}}
 */
function getQueryOptions({ query = product, variables = {} }) {
  return {
    locale: cnvs.store.language,
    query,
    variables: {
      country: cnvs.store.country,
      first: 250,
      ...variables,
    },
  }
}

/**
 * Query a collection from GraphQL API.
 * @param {String} handle - The collection handle to query.
 * @returns {Promise<{description: *, handle: *, id: String, title: *, url: string}>}
 */
export async function queryCollection(handle = '') {
  if (!handle) {
    return
  }

  try {
    const options = getQueryOptions({
      query: collection,
      variables: {
        handle,
        identifiers: collection_metafield_identifiers,
      },
    })

    const response = await cnvs.Query(options)

    if (!response) {
      return
    }
    return formatCollection(response)
  }
  catch (error) {
    throw new Error(error.message, error)
  }
}

/**
 * Query products from GraphQL API.
 * @param {Array} identifiers - The collection handles to query.
 * @param {Object} options - Options for querying.
 * @param {String} options.query - The query file name such as 'collectionImage'.
 * @returns {Promise<Array[]>} Promise object represents an array of collections.
 */
export async function queryCollections(identifiers = []) {
  if (!identifiers.length) {
    return
  }

  try {
    const queue = identifiers.map(value => {
      let modifiedValue = value

      const options = getQueryOptions({
        query: collection,
        variables: {
          handle: modifiedValue,
          identifiers: collection_metafield_identifiers,
        },
      })

      return cnvs.Query(options)
    })

    const response = await Promise.allSettled(queue)

    return response.map(promise => {
      if (promise.status !== 'fulfilled') {
        return false
      }

    return formatCollection(promise.value)

    }).filter(Boolean)
  }
  catch (error) {
    throw new Error(error.message, error)
  }
}

/**
 * Query a product from GraphQL API.
 * @param {Number} id - The product id to query.
 * @returns {Promise<Object>} Promise object represents the product.
 */
export async function queryProductById(id = 0) {
  if (!id) {
    return
  }

  try {
    const options = getQueryOptions({
      query: getProductById,
      variables: {
        id: `gid://shopify/Product/${id}`,
        identifiers: product_metafield_identifiers,
        variant_identifiers: variant_metafield_identifiers,
      }
    })
    const response = await cnvs.Query(options)
    response.product.id = `gid://shopify/Product/${id}`
    return formatProduct(response)
  }
  catch (error) {
    throw new Error(error.message, error)
  }
}


/**
 * Query a product from GraphQL API.
 * @param {String} handle - The product handle to query.
 * @returns {Promise<Object>} Promise object represents the product.
 */
export async function queryProduct(handle = '') {
  if (!handle) {
    return
  }

  try {
    const options = getQueryOptions({ variables: {
      handle,
      identifiers: product_metafield_identifiers,
      variant_identifiers: variant_metafield_identifiers,
    } })
    const response = await cnvs.Query(options)
    return formatProduct(response)
  }
  catch (error) {
    throw new Error(error.message, error)
  }
}

/**
 * Query products from GraphQL API.
 * @param {Array} identifiers - The product handles or IDs to query.
 * @param {Object} options - Options for querying.
 * @param {String} options.type - The type to query by ('handle' or 'id').
 * @param {Boolean} options.useGid - Query by full gid string?
 * @param {String} options.query - The query file name such as 'product'.
 * @returns {Promise<Array[]>} Promise object represents an array of products.
 */
export async function queryProducts(
  identifiers = [],
  amountOfVariants = 100,
  options = {
    query: 'product',
    type: 'handle',
    useGid: false,
  }) {
  if (!identifiers.length) {
    return
  }

  const { query, type, useGid } = options

  try {
    const queue = identifiers.map(value => {
      let modifiedValue = value
      if (type === 'id' && !useGid) {
        modifiedValue = `gid://shopify/Product/${value}`
      }
      const options = getQueryOptions({
        query: queries[query],
        variables: {
          [type]: modifiedValue,
          identifiers: product_metafield_identifiers,
          variant_identifiers: variant_metafield_identifiers,
          amount_of_variants: amountOfVariants,
        },
      })

      const response = cnvs.Query(options).then(result => {
        if (query === 'getProductById' && result && result.product) {
          result.product.id =  modifiedValue
        }
        return result
      })
      return response
    })

    const response = await Promise.allSettled(queue)

    return response.map(promise => {
      if (promise.status !== 'fulfilled') {
        return false
      }

      return formatProduct(promise.value)
    }).filter(Boolean)
  }
  catch (error) {
    throw new Error(error.message, error)
  }
}
